import React from 'react';
import classNames from 'classnames';
import './vehicle-registration-plate.scss';
import { regNumEnToRu } from '../../../utils/utils';

const RU_ALLOWED_LETTERS = 'ABEKMHOPCTYXАВЕКМНОРСТУХ';

const parseArmenianLpn = rawValue => {
  const normalized = String(rawValue || '')
    .replace(/[\s-]+/g, '')
    .toUpperCase();

  const match = normalized.match(/^([0-9*]{2})([A-Z*]{2})([0-9*]{3})$/);
  if (match) {
    return {
      region: match[1],
      series: match[2],
      number: match[3],
      fallback: '',
    };
  }

  return {
    region: '',
    series: '',
    number: '',
    fallback: String(rawValue || '').trim(),
  };
};

const parseAzerbaijaniLpn = rawValue => {
  const normalized = String(rawValue || '')
    .replace(/[\s-]+/g, '')
    .toUpperCase();

  const match = normalized.match(/^([0-9*]{2})([A-Z*]{2})([0-9*]{3})$/);
  if (match) {
    return {
      region: match[1],
      series: match[2],
      number: match[3],
      fallback: '',
    };
  }

  return {
    region: '',
    series: '',
    number: '',
    fallback: String(rawValue || '').trim().toUpperCase(),
  };
};

const parseRussianLikeLpn = rawValue => {
  const original = String(rawValue || '').trim();
  const normalized = original.replace(/[\s-]+/g, '').toUpperCase();
  const strictRegex = new RegExp(`^([${RU_ALLOWED_LETTERS}])(\\d{3})([${RU_ALLOWED_LETTERS}]{2})(\\d{2,3})$`);
  const strict = normalized.match(strictRegex);

  if (strict) {
    return {
      char: strict[1],
      number: strict[2],
      suffix: strict[3],
      region: strict[4],
      fallback: original,
    };
  }

  return {
    char: '',
    number: normalized,
    suffix: '',
    region: '',
    fallback: original,
  };
};

const parseRussianYellowLpn = rawValue => {
  const original = String(rawValue || '').trim();
  const normalized = original.replace(/[\s-]+/g, '').toUpperCase();
  const strictRegex = new RegExp(`^([${RU_ALLOWED_LETTERS}]{2})(\\d{3})(\\d{2,3})$`);
  const strict = normalized.match(strictRegex);

  if (strict) {
    return {
      char: strict[1],
      number: strict[2],
      suffix: '',
      region: strict[3],
      fallback: original,
    };
  }

  return {
    char: '',
    number: normalized,
    suffix: '',
    region: '',
    fallback: original,
  };
};

const parseRussianBlueLpn = rawValue => {
  const original = String(rawValue || '').trim();
  const normalized = original.replace(/[\s-]+/g, '').toUpperCase();
  const strictRegex = new RegExp(`^([${RU_ALLOWED_LETTERS}])(\\d{4})(\\d{2,3})$`);
  const strict = normalized.match(strictRegex);

  if (strict) {
    return {
      char: strict[1],
      number: strict[2],
      suffix: '',
      region: strict[3],
      fallback: original,
    };
  }

  return {
    char: '',
    number: normalized,
    suffix: '',
    region: '',
    fallback: original,
  };
};

const parseRussianBlackLpn = rawValue => {
  const original = String(rawValue || '').trim();
  const normalized = original.replace(/[\s-]+/g, '').toUpperCase();

  const digitsFirst = new RegExp(`^(\\d{4})([${RU_ALLOWED_LETTERS}]{2})(\\d{2,3})$`).exec(normalized);
  if (digitsFirst) {
    return {
      char: '',
      number: digitsFirst[1],
      suffix: digitsFirst[2],
      region: digitsFirst[3],
      fallback: original,
    };
  }

  const lettersFirst = new RegExp(`^([${RU_ALLOWED_LETTERS}]{2})(\\d{4})(\\d{2,3})$`).exec(normalized);
  if (lettersFirst) {
    return {
      char: lettersFirst[1],
      number: lettersFirst[2],
      suffix: '',
      region: lettersFirst[3],
      fallback: original,
    };
  }

  return {
    char: '',
    number: normalized,
    suffix: '',
    region: '',
    fallback: original,
  };
};

const parseRussianRedLpn = rawValue => {
  const original = String(rawValue || '').trim();
  const normalized = original.replace(/[\s-]+/g, '').toUpperCase();

  const cdPrefix = /^([CD]{2})(\d{3})(\d{2,3})$/.exec(normalized);
  if (cdPrefix) {
    return {
      char: cdPrefix[1],
      number: cdPrefix[2],
      suffix: '',
      region: cdPrefix[3],
      fallback: original,
      keepLatin: true,
    };
  }

  const cdMiddle = /^(\d{3})(CD\d)(\d{2,3})$/.exec(normalized);
  if (cdMiddle) {
    return {
      char: '',
      number: cdMiddle[1],
      suffix: cdMiddle[2],
      region: cdMiddle[3],
      fallback: original,
      keepLatin: true,
    };
  }

  const dtMiddle = /^(\d{3})([DT]\d{3})(\d{2,3})$/.exec(normalized);
  if (dtMiddle) {
    return {
      char: '',
      number: dtMiddle[1],
      suffix: dtMiddle[2],
      region: dtMiddle[3],
      fallback: original,
      keepLatin: true,
    };
  }

  return {
    char: '',
    number: normalized,
    suffix: '',
    region: '',
    fallback: original,
    keepLatin: true,
  };
};

const formatLetters = (value, keepLatin = false) => {
  const normalized = String(value || '').toUpperCase();
  return keepLatin ? normalized : regNumEnToRu(normalized);
};

export default function VehicleRegistrationPlate(props) {
  const { item = {} } = props;
  const lpn = item || {};
  const {
    lpn_type = 'ru',
    vehicle_type = 'car',
    lpn_parsed = '',
  } = lpn;

  const lpnType = String(lpn_type || 'ru').toLowerCase();
  const sourceLpn = lpn_parsed || lpn.lpn || '';

  if (!sourceLpn) return null;

  if (lpnType === 'am') {
    const amLpn = parseArmenianLpn(sourceLpn);

    return (
      <div className={classNames('gov-number', lpnType)}>
        <div className="gov-number-am">
          <div className="gov-number-am-badge">
            <div className="gov-number-am-flag">
              <div className="strip" />
              <div className="strip" />
              <div className="strip" />
            </div>
            <div className="gov-number-am-country">AM</div>
          </div>
          <div className="gov-number-am-number">
            {amLpn.region ? <span className="gov-number-am-part">{amLpn.region}</span> : null}
            {amLpn.series ? <span className="gov-number-am-part">{amLpn.series}</span> : null}
            {amLpn.number ? <span className="gov-number-am-part">{amLpn.number}</span> : null}
            {!amLpn.region && !amLpn.series && amLpn.fallback ? <span className="gov-number-am-part fallback">{amLpn.fallback}</span> : null}
          </div>
        </div>
      </div>
    );
  }

  if (lpnType === 'az') {
    const azLpn = parseAzerbaijaniLpn(sourceLpn);
    const hasParsed = Boolean(azLpn.region && azLpn.series && azLpn.number);

    return (
      <div className={classNames('gov-number', lpnType)}>
        <div className="gov-number-az">
          <div className="gov-number-az-badge">
            <div className="gov-number-az-flag">
              <div className="strip" />
              <div className="strip" />
              <div className="strip" />
            </div>
            <div className="gov-number-az-country">AZ</div>
          </div>
          <div className="gov-number-az-number">
            {hasParsed ? (
              <>
                <span className="gov-number-az-part">{azLpn.region}</span>
                <span className="gov-number-az-divider">-</span>
                <span className="gov-number-az-part">{azLpn.series}</span>
                <span className="gov-number-az-divider">-</span>
                <span className="gov-number-az-part">{azLpn.number}</span>
              </>
            ) : (
              <span className="gov-number-az-fallback">{azLpn.fallback}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const parsed = (() => {
    if (lpnType === 'ru_yellow') return parseRussianYellowLpn(sourceLpn);
    if (lpnType === 'ru_blue') return parseRussianBlueLpn(sourceLpn);
    if (lpnType === 'ru_black') return parseRussianBlackLpn(sourceLpn);
    if (lpnType === 'ru_red') return parseRussianRedLpn(sourceLpn);
    return parseRussianLikeLpn(sourceLpn);
  })();

  const number = parsed.number || sourceLpn;
  const char = parsed.char || '';
  const charSecond = parsed.suffix || '';
  const region = parsed.region || '';
  const country = lpnType && String(lpnType).includes('ru') ? 'RUS' : String(lpnType).toUpperCase();
  const isLongRegion = region.toString().length === 3;
  const isMoto = vehicle_type === 'motorcycle';
  const keepLatin = Boolean(parsed.keepLatin);

  const renderFlag = () => (
    <div className="gov-number-region-flag">
      <div className="strip" />
      <div className="strip" />
      <div className="strip" />
    </div>
  );

  return (
    <div className={classNames('gov-number', lpnType)}>
      <div className={classNames('gov-number-container', { long: isLongRegion })}>
        <div className="gov-number-number">
          <div className="gov-number-number-container">
            {char ? <div className="gov-number-number-char">{formatLetters(char, keepLatin)}</div> : null}
            <div className="gov-number-number-int">{number}</div>
            {charSecond && !isMoto ? <div className="gov-number-number-char">{formatLetters(charSecond, keepLatin)}</div> : null}
          </div>
        </div>
        <div className="gov-number-region">
          {isMoto ? <div className="gov-number-number-char">{formatLetters(charSecond, keepLatin)}</div> : null}
          <div className="gov-number-region-number">
            {region}
            <div className="gov-number-region-container">
              <div className="gov-number-region-name">
                {country}
              </div>
              {country === 'RUS' ? renderFlag() : null}
            </div>
          </div>
          <div className="gov-number-region-container">
            <div className="gov-number-region-name">
              {country}
            </div>
            {country === 'RUS' ? renderFlag() : null}
          </div>
        </div>
      </div>
    </div>
  );
}
